import logo from '/ellio-logo.png';
import logoOnly from '/ellio-logo-only.png';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { ChevronsLeft, ChevronsRight, Shield } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { consoleMenuSections } from '../console.menus';
import useConsoleMenu from '../hooks/use-console-menu';

function ConsoleBadge({ collapsed }: { collapsed: boolean }) {
  return (
    <div
      className={cn(
        'bg-primary-10 text-base-orange-40 mx-[22px] mt-4 flex items-center gap-2 rounded-md px-3 py-2 text-xs font-bold tracking-wide uppercase',
        collapsed && 'mx-2 justify-center px-0'
      )}
    >
      <Shield className="size-4 flex-none" />
      {!collapsed && <span>Superadmin Console</span>}
    </div>
  );
}

function ConsoleSidebar() {
  const navigate = useNavigate();

  const {
    collapseSidebar,
    setCollapseSidebar,
    accordion,
    setAccordion,
    currentChildMenu,
    isMenuActive,
  } = useConsoleMenu();

  useEffect(() => {
    if (collapseSidebar) {
      setAccordion(undefined);
    }
  }, [collapseSidebar, setAccordion]);

  const renderSections = (mobile: boolean) =>
    consoleMenuSections.map((section, sectionIndex) => (
      <div
        key={section.id}
        className="border-neutral-80 border-b last:border-b-0"
      >
        {section.label && (!collapseSidebar || mobile) && (
          <div className="text-text-tertiary px-[22px] pt-6 pb-2 text-lg font-semibold">
            {section.label}
          </div>
        )}

        <ul
          className={cn(
            'space-y-2 px-[22px]',
            sectionIndex === 0 ? 'py-8' : 'pb-8',
            section.label && (!collapseSidebar || mobile) ? 'pt-2' : 'pt-8'
          )}
        >
          <Accordion
            type="single"
            collapsible
            value={accordion ?? ''}
            onValueChange={setAccordion}
          >
            {section.menus.map(menu => {
              const visibleChildren = menu.children ?? [];
              const hasChildren = visibleChildren.length > 0;
              const menuActive = isMenuActive(menu);

              return (
                <AccordionItem
                  key={menu.label}
                  value={menu.label}
                  className="border-0"
                >
                  <AccordionTrigger
                    className={cn(
                      'hover:bg-primary-10 hover:text-base-orange-40 text-text-tertiary cursor-pointer border-0 p-2 hover:no-underline',
                      !hasChildren && '[&>svg]:hidden',
                      !mobile && collapseSidebar && '[&>svg]:hidden',
                      menuActive && 'text-base-orange-40 bg-primary-10'
                    )}
                    onClick={() => {
                      if (!mobile && collapseSidebar) {
                        setCollapseSidebar(false);
                      }
                      if (!hasChildren) {
                        navigate(menu.route);
                        if (mobile) setCollapseSidebar(true);
                      } else {
                        setAccordion(menu.label);
                      }
                    }}
                  >
                    <Tooltip>
                      <li
                        className={cn(
                          'flex flex-none items-center gap-3 [&>svg]:size-4',
                          !mobile && collapseSidebar && '[&>p]:hidden'
                        )}
                      >
                        <TooltipTrigger asChild>
                          {menu.icon && <menu.icon />}
                        </TooltipTrigger>
                        <p>{menu.label}</p>
                      </li>

                      <TooltipContent side="right">{menu.label}</TooltipContent>
                    </Tooltip>
                  </AccordionTrigger>

                  {hasChildren && (
                    <AccordionContent>
                      <ul>
                        {visibleChildren.map(child => {
                          const isActive = currentChildMenu === child.label;
                          return (
                            <li key={child.label}>
                              <button
                                type="button"
                                onClick={() => {
                                  navigate(child.route);
                                  if (mobile) setCollapseSidebar(true);
                                }}
                                className={cn(
                                  'text-text-tertiary hover:text-base-orange-40 flex w-full cursor-pointer py-2 pl-9 font-medium',
                                  isActive && 'text-base-orange-40',
                                  !mobile && collapseSidebar && '[&>p]:hidden'
                                )}
                              >
                                <p className="flex flex-none">{child.label}</p>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </AccordionContent>
                  )}
                </AccordionItem>
              );
            })}
          </Accordion>
        </ul>
      </div>
    ));

  return (
    <>
      <aside
        className={cn(
          'sticky top-0 hidden h-screen w-72 overflow-x-hidden overflow-y-auto bg-neutral-100 transition-all duration-300 md:block',
          collapseSidebar && 'w-[72px]'
        )}
      >
        <div
          className={cn(
            'border-neutral-80 relative flex items-center border-b py-12',
            collapseSidebar ? 'px-2' : 'px-9'
          )}
        >
          <img
            src={collapseSidebar ? logoOnly : logo}
            alt="logo-example"
            className={cn('flex-none', collapseSidebar ? 'w-8' : 'w-28')}
          />
          <button
            className="bg-neutral-90 text-text-primary absolute right-0 cursor-pointer rounded-l-sm px-1 py-2"
            type="button"
            onClick={() => setCollapseSidebar(prev => !prev)}
          >
            <ChevronsLeft className="size-3.5" />
          </button>
        </div>

        <ConsoleBadge collapsed={collapseSidebar} />

        <nav>{renderSections(false)}</nav>
      </aside>

      <div
        className={cn(
          'relative h-0 w-0 md:hidden',
          !collapseSidebar && 'fixed z-20 block h-screen w-full bg-black/20'
        )}
      >
        <button
          className="bg-neutral-80 absolute top-[21px] -right-5 z-50 rounded-r-md px-1 py-2"
          type="button"
          onClick={() => setCollapseSidebar(prev => !prev)}
        >
          <ChevronsRight className="size-3" />
        </button>
        <aside
          className={cn(
            'sticky top-0 h-screen w-0 overflow-x-hidden overflow-y-auto bg-neutral-100 transition-all duration-300',
            !collapseSidebar && 'h-screen w-10/12'
          )}
        >
          <div className="border-neutral-80 relative flex min-h-32 items-center border-b px-9 py-12">
            <img src={logo} alt="logo-example" className="w-25 flex-none" />
            <button
              className="bg-neutral-90 text-text-primary absolute right-0 cursor-pointer rounded-l-sm px-1 py-2"
              type="button"
              onClick={() => setCollapseSidebar(prev => !prev)}
            >
              <ChevronsLeft className="size-3.5" />
            </button>
          </div>

          <ConsoleBadge collapsed={false} />

          <nav>{renderSections(true)}</nav>
        </aside>
      </div>
    </>
  );
}

export { ConsoleSidebar };
